/**
 * `loop-trace`: one line of code and the table a reader would keep on paper,
 * walked one iteration at a time.
 *
 * <div class="pc-widget" data-widget="loop-trace"
 *      data-code="for (int i = 1; i &lt;= 3; i++) sum += i;"
 *      data-columns="[&quot;i&quot;,&quot;sum&quot;]" data-rows="[[1,1],[2,3],[3,6]]"
 *      data-caption="sum holds the total after each pass.">
 *   <p class="pc-widget-fallback">i = 1 gives sum 1, i = 2 gives 3, i = 3 gives 6.</p>
 * </div>
 *
 * Two optional attributes (`LN-WGT03`, asked for by `LN-JS-M4`), so a row can
 * be something other than a pass; a block without them is the trace it
 * always was:
 *
 * - `data-row-label`: what a row is, in the lesson's language ("step" for a
 *   bytecode instruction, "run" for one run of an inner body). It heads the
 *   numbering column and names the step in the readout, in place of "pass".
 * - `data-row-kinds`: a JSON list with one entry per row, `"pass"` or
 *   `"check"`. A check row (the loop's last test, the one that answers false)
 *   is drawn as a check and takes no number, so the passes count 1, 2, 3.
 *   A list of the wrong length or with another value leaves the fallback.
 *
 * A long trace scrolls inside the widget, never the page, and the stepper
 * keeps the current row in view.
 */

import { asText, attachStepper, buildShell, el, isStringOrNumber, parseJson, type WidgetText } from "./dom";

const isColumns = (value: unknown): value is string[] =>
  Array.isArray(value) && value.length > 0 && value.every((v) => typeof v === "string");

const isRows = (value: unknown): value is (string | number)[][] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every((row) => Array.isArray(row) && row.every(isStringOrNumber));

type RowKind = "pass" | "check";

const isKinds =
  (rows: number) =>
  (value: unknown): value is RowKind[] =>
    Array.isArray(value) && value.length === rows && value.every((kind) => kind === "pass" || kind === "check");

export function renderLoopTrace(host: HTMLElement, text: WidgetText): boolean {
  const columns = parseJson(host.dataset.columns, isColumns);
  const rows = parseJson(host.dataset.rows, isRows);
  if (!columns || !rows) return false;
  let kinds: RowKind[] = rows.map(() => "pass");
  if (host.dataset.rowKinds !== undefined) {
    const authored = parseJson(host.dataset.rowKinds, isKinds(rows.length));
    if (!authored) return false;
    kinds = authored;
  }
  const rowLabel = host.dataset.rowLabel?.trim() || null;

  // Each pass row's number; a check row has none.
  let counted = 0;
  const numbers = kinds.map((kind) => (kind === "pass" ? ++counted : 0));
  const labelOf = (index: number) =>
    kinds[index] === "check"
      ? text("checkRow")
      : rowLabel
        ? text("rowOf", { label: rowLabel, n: numbers[index] })
        : text("pass", { n: numbers[index] });

  const shell = buildShell(host, {
    label: text("labelLoopTrace"),
    code: host.dataset.code,
    caption: host.dataset.caption,
  });

  const scroller = el("div", "pc-trace-scroll");
  const table = el("table");
  const thead = el("thead");
  const headRow = el("tr");
  headRow.append(el("th", undefined, rowLabel ?? text("passColumn")));
  for (const column of columns) headRow.append(el("th", undefined, column));
  thead.append(headRow);
  table.append(thead);

  const tbody = el("tbody");
  const bodyRows = rows.map((row, index) => {
    const tr = el("tr");
    if (kinds[index] === "check") tr.classList.add("is-check");
    tr.append(el("td", undefined, kinds[index] === "check" ? text("checkRow") : String(numbers[index])));
    for (let i = 0; i < columns.length; i++) {
      tr.append(el("td", undefined, row[i] === undefined ? "" : asText(row[i])));
    }
    tbody.append(tr);
    return tr;
  });
  table.append(tbody);
  scroller.append(table);
  shell.body.append(scroller);

  attachStepper(
    shell,
    rows.length,
    (step) => {
      bodyRows.forEach((tr, index) => {
        tr.classList.toggle("is-active", index === step);
        tr.classList.toggle("is-dim", index > step);
      });
      // Scroll the trace, not the page: `scrollIntoView` would move the page too.
      const active = bodyRows[step];
      const head = thead.offsetHeight;
      if (active.offsetTop - head < scroller.scrollTop) {
        scroller.scrollTop = active.offsetTop - head;
      } else if (active.offsetTop + active.offsetHeight > scroller.scrollTop + scroller.clientHeight) {
        scroller.scrollTop = active.offsetTop + active.offsetHeight - scroller.clientHeight;
      }
    },
    text,
    { stepLabel: labelOf },
  );
  return true;
}
